import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { unifiedDashboardNav } from "./navigationConfig";
import { useSessionPersistence } from "../../hooks/useSessionPersistence";
import LogoutIcon from '../../assets/icons/logout-icon.svg';

type Props = {
  children: React.ReactNode;
};

function classNames(...xs: Array<string | false | null | undefined>) {
  return xs.filter(Boolean).join(" ");
}

const DESKTOP_QUERY = "(min-width: 1024px)";

/**
 * Responsive app shell navigation.
 * Desktop uses a collapsible sidebar; mobile uses an overlay sheet.
 * Header height is measured so the floating mobile trigger stays below the app header.
 */
export default function Navigation({ children }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logoutSession } = useSessionPersistence();

  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia(DESKTOP_QUERY).matches;
  });

  // Open by default on desktop, closed by default on mobile
  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia(DESKTOP_QUERY).matches;
  });

  // Fallback keeps the trigger positioned reasonably before first measurement.
  const [headerHeight, setHeaderHeight] = useState<number>(70);

  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_QUERY);

    // Desktop/mobile transitions reset nav to the intended default state for that breakpoint.
    const sync = () => {
      const desktop = mql.matches;
      setIsDesktop(desktop);
      setIsOpen(desktop);
    };

    sync();
    mql.addEventListener("change", sync);
    return () => mql.removeEventListener("change", sync);
  }, []);

  // Track header height so the floating mobile trigger always sits below it.
  useEffect(() => {
    const headerEl = document.getElementById("app-header");
    if (!headerEl) return;

    const measure = () => setHeaderHeight(headerEl.getBoundingClientRect().height);

    measure();

    const ro = new ResizeObserver(() => measure());
    ro.observe(headerEl);

    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  const navWidth = useMemo(() => (isOpen ? "w-70" : "w-14"), [isOpen]);

  const hamburgerTop = headerHeight + 12;

  return (
    <div
      className="flex w-full overflow-hidden bg-stone-50 antialiased"
      style={{ height: `calc(100dvh - ${headerHeight}px)` }}
    >
      <div className="flex flex-1 min-h-0 min-w-0 w-full">
        {/* Mobile-only floating trigger when the sheet is closed */}
        {!isDesktop && !isOpen && (
          <button
            type="button"
            onClick={() => setIsOpen(true)}
            aria-label="Open navigation"
            data-tour="planner-mobile-nav-trigger"
            className="fixed left-[12px] z-50 w-9 h-9 flex flex-col justify-center items-center space-y-[3px] rounded-md bg-white border border-stone-200 shadow-sm hover:bg-stone-50"
            style={{ top: `calc(${hamburgerTop}px + env(safe-area-inset-top,0px))` }}
          >
            <div className="w-[17px] h-0.5 bg-stone-500" />
            <div className="w-[17px] h-0.5 bg-stone-500" />
            <div className="w-[17px] h-0.5 bg-stone-500" />
          </button>
        )}

        {/* Mobile-only backdrop behind the open nav sheet */}
        {!isDesktop && isOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/20"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Left nav: inline sidebar on desktop, overlay sheet on mobile */}
        <aside
          data-tour="planner-left-nav"
          className={classNames(
            "border-r border-stone-200 bg-white transition-all duration-300 flex flex-col overflow-hidden",

            isDesktop && "shrink-0",
            isDesktop && navWidth,

            !isDesktop &&
              "fixed left-0 top-0 h-[calc(100vh)] z-50 w-70 shadow-lg transition-transform duration-300 pt-[env(safe-area-inset-top,0px)]",
            !isDesktop && (isOpen ? "translate-x-0" : "-translate-x-full")
          )}
        >
          <div
            className={classNames(
              "shrink-0 z-10 bg-white flex items-center px-3 pt-2 pb-2 border-b border-stone-100",
              isDesktop
                ? isOpen
                  ? "justify-start"
                  : "justify-center"
                : "justify-start"
            )}
          >
            <button
              type="button"
              onClick={() => setIsOpen((v) => !v)}
              className="w-9 h-9 flex flex-col justify-center items-center space-y-[3px] rounded-md hover:bg-stone-200 transition-colors"
              aria-label="Toggle menu"
            >
              <div className="w-[17px] h-0.5 bg-stone-500" />
              <div className="w-[17px] h-0.5 bg-stone-500" />
              <div className="w-[17px] h-0.5 bg-stone-500" />
            </button>
          </div>

          {/* Nav content is hidden when collapsed so desktop behaves like an icon rail */}
          {isOpen && (
            <div className="flex flex-col min-h-0 h-full">
              <div className="flex-1 overflow-y-auto px-4 py-4">
                {unifiedDashboardNav.map((group) => (
                  <div key={group.title} className="mb-5 font-sans">
                    <div className="mb-[12px] text-sm font-semibold uppercase text-stone-800">
                      {group.title}
                    </div>

                    {group.sections.map((section, idx) => (
                      <div
                        key={`${group.title}-${section.title || idx}`}
                        className="mb-7"
                      >
                        {section.title ? (
                          <div className="mb-2 text-sm font-medium text-stone-800">
                            {section.title}
                          </div>
                        ) : null}

                        <div className="space-y-3">
                          {section.items.map((item) => {
                            const isActive =
                              item.match === "exact"
                                ? location.pathname === item.path
                                : location.pathname.startsWith(item.path);

                            return (
                              <NavLink
                                key={item.path}
                                to={item.disabled ? location.pathname : item.path}
                                aria-disabled={item.disabled ? true : undefined}
                                tabIndex={item.disabled ? -1 : 0}
                                className={classNames(
                                  "rounded-md px-4 py-[2px] text-sm leading-6 font-medium transition-colors flex items-center gap-2",
                                  item.disabled && "opacity-50 cursor-not-allowed",
                                  !item.disabled && "hover:bg-stone-200",
                                  isActive &&
                                    !item.disabled &&
                                    "bg-cyan-100 text-stone-900",
                                  !isActive && "text-stone-700"
                                )}
                                onClick={(e) => {
                                  if (item.disabled) e.preventDefault();

                                  // Mobile navigation closes after a successful route selection.
                                  if (!isDesktop && !item.disabled) setIsOpen(false);
                                }}
                              >
                                {item.icon && (
                                  <img src={item.icon} alt={item.label} className="w-5 h-5" />
                                )}
                                {item.label}
                              </NavLink>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              <div className="shrink-0 border-t border-stone-200 px-4 py-4 bg-white mt-auto">
                <button
                  onClick={async () => {
                    try {
                      console.log('[Navigation] Initiating secure logout...');
                      localStorage.removeItem('account-token');
                      localStorage.removeItem('account-refresh-token');
                      logoutSession();
                      setIsOpen(false);
                      navigate('/login');
                    } catch (error) {
                      console.error('[Navigation] Logout error:', error);
                      localStorage.removeItem('account-token');
                      logoutSession();
                      setIsOpen(false);
                      navigate('/login');
                    }
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-md transition-all duration-200 hover:bg-cyan-50 hover:shadow-sm group"
                >
                  <img src={LogoutIcon} alt="Logout" className="w-5 h-5" />
                  <span className="text-stone-800 font-medium group-hover:text-cyan-700 transition-colors">
                    Logout
                  </span>
                </button>
              </div>
            </div>
          )}
        </aside>

        {/* Main content stays full width and gets extra top padding on mobile for the floating trigger */}
        <main
          id="main-content"
          className={classNames(
            "flex-1 min-w-0 min-h-0 overflow-y-auto",
            !isDesktop && "pt-12"
          )}
        >
          <div className="px-6 pt-6 pb-[max(1.5rem,calc(env(safe-area-inset-bottom,0px)+0.75rem))]">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}